import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// ── Impersonation isolation ──
// admin-impersonate-vendor.js redirects to /vendor/impersonate-session after
// a successful magic-link login. Every tab previously shared the same
// default Supabase client (localStorage-backed, one fixed storage key), so
// a vendor session landing in a new tab could sync into the admin's own tab
// via Supabase's cross-tab `storage` event listener — silently swapping the
// admin's session for the vendor's there.
//
// This was originally detected via a `?impersonating=1` query param, but
// that turned out to be unreliable: the redirect preserved the /vendor/...
// PATH but dropped the query string somewhere along the way (almost
// certainly the Supabase redirect-URL allow-list normalizing the URL), so
// the marker never actually reached the frontend. Detecting a dedicated
// PATH instead avoids that — paths are exactly what redirect allow-lists
// are built to match reliably, with no query string to lose.
//
// A tab that lands on the callback path gets its own client, backed by
// sessionStorage under a distinct key instead of the shared localStorage
// one. sessionStorage is tab-scoped and never fires cross-tab storage
// events, so the admin's tab never hears about it, and this tab's vendor
// session disappears on its own once the tab is closed.
//
// The marker is mirrored into this tab's own sessionStorage the first time
// it's seen, so the isolation survives client-side navigation away from
// the callback path (e.g. once ImpersonationCallback.jsx redirects to
// /vendor/dashboard) or a manual refresh — sessionStorage persists for the
// tab's whole lifetime regardless of which page is currently showing.
const IMPERSONATION_FLAG_KEY = "bb_impersonating";
const IMPERSONATION_CALLBACK_PATH = "/vendor/impersonate-session";

const isImpersonationCallback =
  typeof window !== "undefined" &&
  window.location.pathname === IMPERSONATION_CALLBACK_PATH;

if (isImpersonationCallback && typeof window !== "undefined") {
  window.sessionStorage.setItem(IMPERSONATION_FLAG_KEY, "1");
}

const isImpersonating =
  typeof window !== "undefined" &&
  window.sessionStorage.getItem(IMPERSONATION_FLAG_KEY) === "1";

const clientOptions = isImpersonating
  ? {
      auth: {
        storage: window.sessionStorage,
        storageKey: "sb-impersonation-auth-token",
        persistSession: true,
        detectSessionInUrl: true,
      },
    }
  : {};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, clientOptions)